// W42 · 屏幕类型 —— the load-bearing change of `docs/spec/w42-screen-type-color.md`
// W1/W2 (ADR-0107).
//
// W41 put colour on **opt-in**: `card.tsx` kept `bg-paper-elevated`, and three
// components in the whole app reached for a `surface-*` token by hand. The route
// audit found the result — `surface-coral` 0 components, `chrome-navy` 0
// components, and 39 routes defaulting to white. That was not an execution
// failure, it was a design failure: W41 §1 E8 specified 「每页一个饱和色块」 and
// the build implemented it exactly, so `dashboard` sat under the density floor
// (1 coloured card out of 11) and `anniversaries` over it (a whole 7-row list
// dyed at once).
//
// W42 moves the rule **up from the component to the screen**, so a route
// inherits its colour from what kind of screen it is rather than from how many
// times a component remembered to ask. Two types, and only two:
//
//   `feeling` — 首頁 / 日記 / 紀念日 / 心願 (spec F5). One saturated peak card,
//                pale `tint-blush` for everything smaller, one mascot.
//   `data`    — everything else (spec F6). No saturated card at all; pale
//                `tint-icy` for small surfaces, white for large ones, and
//                translucent glass for the floating layer so the page wash is
//                actually visible through it (F12).
//
// **Why a table of route roots and not a per-route prop.** W1 requires 39 routes
// to inherit without opt-in. A prop would be opt-in with extra steps, and the
// next person to add a route would leave it unset. A prefix table makes the
// declaration *data* — reviewable in one place, and a new feeling-type screen is
// a one-line change with an obvious blast radius.
//
// **Why the default is `data`.** `DEFAULT_SCREEN_TYPE` is the conservative
// choice in the direction spec W1 names: a new page must not become pink just
// because nobody got round to classifying it. The cost of being wrong is a white
// screen; the cost of defaulting to `feeling` is a screen that looks deliberate
// when nobody decided it.
//
// **Why the provider lives in another file.** `eslint --max-warnings=0` runs
// `react-refresh/only-export-components`, and a module exporting both a
// component and plain data falls back to a full reload on every edit. This file
// therefore exports **no component at all** — the same reason `mascots.ts` holds
// the mascot data and `hooks/useIsMobile.ts` holds a hook. The provider that
// consumes this context is `components/system/ScreenTypeProvider.tsx`.

import { createContext, useContext } from "react";
import type { ReactNode } from "react";

/** A screen that carries one saturated peak card + a mascot (spec F5). */
export const SCREEN_FEELING = "feeling";
/** A screen with no saturated card; small surfaces are pale, large are white (spec F6). */
export const SCREEN_DATA = "data";

export type ScreenType = typeof SCREEN_FEELING | typeof SCREEN_DATA;

/**
 * The type a screen gets when nothing classifies it. See the file header: a new
 * page lands white rather than pink.
 */
export const DEFAULT_SCREEN_TYPE: ScreenType = SCREEN_DATA;

/**
 * Route roots that make a screen `feeling` (spec F5, verbatim).
 *
 * These are **prefixes**, matched with the same trailing-slash boundary
 * `isWithinModule` uses, so `/wishes` owns `/wishes/archived` and
 * `/wishes/trash` and a hypothetical `/wishes-archive` stays `data` rather than
 * being dragged in by `startsWith`.
 *
 * `anniversaries` and every other module below is listed by path, not by
 * manifest entry, because the classification is per **screen kind** and cuts
 * across modules: a `data` module (`calendar`) and a `feeling` module
 * (`diaries`) are different things, and the sub-routes of a feeling module stay
 * feeling.
 */
export const FEELING_ROUTE_ROOTS: readonly string[] = Object.freeze([
  "/dashboard",
  "/diaries",
  "/anniversaries",
  "/wishes",
]);

/**
 * Routes that sit **inside** a feeling module but are `data` screens anyway.
 *
 * One entry today, and it is here because the prefix rule got it wrong on its
 * own: `/dashboard/messages` is the daily-message thread, and classifying it
 * `feeling` gave it a pink tint with **no peak card** — a feeling screen
 * violating F16's 「每屏恰好 1 块」 in the direction of zero, which is the one
 * direction F16 does not read as intentional. It is a list of messages with a
 * detail view; the emotional peak of the module is the home screen's
 * `PairHeroCard`, one route over.
 *
 * The alternative — giving `/dashboard/messages` its own peak card — was
 * rejected: F16 caps the palette at four saturated blocks and says the fifth
 * card onward is white. Adding a fifth screen to the peak tier is a larger
 * change than classifying one list screen correctly.
 *
 * Matched with the same trailing-slash boundary, and it is checked **before**
 * the feeling roots so the exception cannot be shadowed by its own module.
 */
export const DATA_ROUTE_ROOTS: readonly string[] = Object.freeze(["/dashboard/messages"]);

/**
 * The saturated peak-card allocation (spec F16 「每屏恰好 1 块」, and W2's
 * 「色彩分配表必须写死」).
 *
 * This is deliberately a **table and not a rule**. F16 exists because W41's
 * 「每页一个饱和色块」 produced a dashboard with one coloured card out of
 * eleven — the rule was followed exactly and the result was still wrong. A rule
 * is what produced that; a named slot per screen is what the reference images
 * actually show. F9 adds the second half: the mascot goes on the same card, and
 * `mascot` must never equal `surface`, or the character disappears into its own
 * card.
 *
 * Three of the four rows were already true before W42 (`PairHeroCard` aqua,
 * `HeroSection` lime, `DiaryHero` ice); W42 adds the `wishes` row, which had no
 * peak card at all, and is what stops that screen from reading as a white list.
 *
 * `coral` is deliberately absent as a **card** fill. F13 restricts it to cards
 * carrying nothing but a large numeral, and no feeling screen this round has
 * one; it survives as the mascot colour on the `wishes` card, where it is a
 * character rather than a background behind text. `docs/audit/contrast-matrix.mjs`
 * keeps the F13 check live and demonstrably red-capable regardless.
 */
export type AccentSurface =
  | "surface-aqua"
  | "surface-lime"
  | "surface-ice"
  | "surface-lilac"
  | "surface-coral";

/** One screen's pale mascot card — the hero. */
export interface HeroSlot {
  /**
   * The **screen**, not the module. The diaries hero is `DiaryHero`, which only
   * renders on `/diaries/:id`; keying it on the module claimed a hero for the
   * list, the create form and the trash list, and "每屏恰好 1 块" became a rule
   * with eight exceptions. A `:param` is legal and matches as a wildcard.
   */
  route: string;
  /** The pale fill. One token, used once per screen — see ADR-0109. */
  tint: "tint-hero";
  /** F9: exactly one mascot per hero, and F17: never its own card's colour. */
  mascot: "coral" | "aqua" | "lilac" | "lime";
}

/** One saturated accent card. */
export interface AccentSlot {
  route: string;
  surface: AccentSurface;
  /**
   * What the card carries, which is what F13 judges. `coral` is 4.76:1 — it
   * clears AA only at 700+ — so it is legal on `number` (a big numeral and
   * nothing else) and illegal on `list` / `message` / `mood`, which carry copy
   * at body size.
   */
  role: "number" | "countdown" | "list" | "mood" | "message";
}

/**
 * W43 · `ADR-0109` — **the pale tone appears once per screen**, on the hero.
 *
 * W42 put it on every secondary card and every list row (§8.5's ruling, "tint 只给
 * 小卡/列表项"). That is defensible in a mockup and wrong on a device: eleven cards
 * each on a faintly pink ground reads as a colour cast over the whole page, not
 * as a palette. The reference solves it by spending the pale tone **once** — the
 * top card that carries the mascot and the primary action — and letting the two
 * or three cards that earn attention carry saturated colour.
 *
 * One token, not one per screen type: both reference heroes are **cool** (pale
 * lilac on Tools, pale ice on Home) and neither is pink, and a cool ground is
 * what lets a saturated mascot sit on it. `--tint-blush` is withdrawn.
 *
 * ### Every hero mascot is `coral`, and that is a measured result
 *
 * F17 asked for a mascot that never shares its card's colour, which four
 * mascots and a pale ground satisfy trivially — and which is why W42's screens
 * looked fine and still had a nearly invisible character. Measured against
 * `#E3F3FF`:
 *
 * | mascot  | vs pale hero | vs white |
 * |---------|-------------|----------|
 * | coral   | **3.16:1**  | 3.58:1   |
 * | lilac   | 2.38:1      | 2.69:1   |
 * | aqua    | 1.89:1      | 2.14:1   |
 * | lime    | **1.05:1**  | 1.19:1   |
 *
 * A mascot is a graphical object (WCAG SC 1.4.11) and the bar is 3:1. Only
 * `#F34A42` clears it, on the pale ground **and** on white — so a white backing
 * disc does not rescue the other three either, and no choice of ground in the
 * palette does. Per-screen variety is therefore not available with the current
 * mascot fills; restoring it means darkening `aqua` / `lilac` / `lime`, which
 * would break their correspondence with the reference characters and is left as
 * a named follow-up rather than faked here.
 */
export const HERO_SLOTS: readonly HeroSlot[] = Object.freeze([
  { route: "/dashboard", tint: "tint-hero", mascot: "coral" },
  { route: "/anniversaries", tint: "tint-hero", mascot: "coral" },
  { route: "/diaries/:id", tint: "tint-hero", mascot: "coral" },
  // W53.2 D-6. The detail page **already had** a card that read as a hero —
  // `AnniversaryHero.tsx` was a hand-written `glass-1 rounded-xl shadow-card`
  // — but it was not a `HeroCard`, so it was in no table, carried no mascot and
  // had nothing asserting it. The anomaly was never "three detail pages are
  // missing a hero"; it was that the one detail page that grew a hero-shaped
  // card did it by hand. Registered so the card can be the primitive instead.
  { route: "/anniversaries/:id", tint: "tint-hero", mascot: "coral" },
  { route: "/wishes", tint: "tint-hero", mascot: "coral" },
]);

/**
 * W53.2 V-3 — detail pages that **deliberately** carry no mascot hero.
 *
 * This table exists because the alternative is silence. Three of the five
 * detail pages have no hero, and the W53.2 audit found that a reader (or agent)
 * looking at `HERO_SLOTS` sees four rows, three detail routes, and no way to
 * tell "we decided this page has no hero" from "nobody got round to it" — the
 * gap is invisible in exactly the place a reviewer would look.
 *
 * So absence is **recorded**, with the reason, next to the rows it contrasts
 * with. Each reason is the actual argument for that page, not a restatement of
 * the decision:
 *
 *  - `/wishes/:id` — a wish detail is a **list of blocks** (progress, budget,
 *    logs, diary links). `MobileCollapsibleSections` groups them by status and
 *    that grouping is the page's hierarchy; a hero above it would push the
 *    actual content below the fold for a card that repeats the title the
 *    `PageHero` breadcrumb already carries.
 *  - `/albums/:id` — a photo grid. A hero is a wide block, and every pixel of
 *    its height is a row of photographs pushed down. The album's own cover
 *    already occupies the hero position visually.
 *  - `/calendar/events/:id` — the subject of the page **is** the event, and it is
 *    already the `PageHero` title and subtitle. A second card saying the same
 *    thing is a duplicate, and F16's 「每屏恰好 1 块」 is about the mascot layer
 *    rather than about there being exactly one title.
 *
 * `/wishes` (the list) and `/anniversaries` (the list) DO have heroes, and so
 * does `/dashboard` — so this is not "detail pages have no heroes". It is three
 * named exceptions with reasons.
 */
export interface NoHeroRoute {
  route: string;
  /** Why this screen has no mascot hero. Never empty — see `detail-page-card-consistency`. */
  reason: string;
}

export const DELIBERATE_NO_HERO: readonly NoHeroRoute[] = Object.freeze([
  Object.freeze({
    route: "/wishes/:id",
    reason:
      "A wish detail is a list of status-grouped blocks; the grouping is the page's " +
      "hierarchy and a hero would push it below the fold repeating the PageHero title.",
  }),
  Object.freeze({
    route: "/albums/:id",
    reason:
      "A photo grid — a hero block is full width and every pixel of its height is a row " +
      "of photographs pushed down. The album cover already occupies that position.",
  }),
  Object.freeze({
    route: "/calendar/events/:id",
    reason:
      "The event is already the PageHero title and subtitle, so a mascot card would be a " +
      "second statement of the same fact.",
  }),
]);

/**
 * Saturated accent cards, in render order, per screen.
 *
 * Hard-coded rather than derived, for the reason W42 learned the hard way: a
 * *rule* about how many saturated cards a screen may have is what produced
 * W41's `dashboard` at one coloured card out of eleven. A named slot per card
 * cannot drift that way.
 *
 * `/anniversaries` is the one whose **shape** changed twice. Its hero was a single
 * lime block three columns wide holding a number, a countdown and a list at once,
 * and lime is 14.24:1 — the most saturated thing the app can render, spent on
 * the least interesting third of the content. W43 split it three ways; W44 then
 * **removed the `list` row**, a saturated teal panel whose only job was to repeat
 * the first row of the `AnniversaryTimeline` sitting directly beneath it. What is
 * left spends lime on a bare numeral, which is the one thing lime is good at.
 */
export const ACCENT_SLOTS: readonly AccentSlot[] = Object.freeze([
  // W57.0: the dashboard's saturated cards are **two**, in render order:
  // 天氣 (ice) sits directly under the hero, and 紀念日 (lime) under the
  // mood pair. The weather card was a saturated block on the screen since
  // W47.1 but was **not in this table** — `WeatherCard` painted
  // `surface-ice` by hand, so it carried no `data-accent-card` and the
  // route audit's "the table declares N, the DOM has M" check could not
  // see it. It is now a real `AccentCard` with a real row here, and the
  // mood card is no longer saturated at all (W57.0: the mood pair is two
  // white cards, and identity comes from the avatars and the emoji).
  //
  // The `role` is `list` because the card carries the three-day forecast —
  // a list of values, not a bare numeral. `assertCoralRole` only restricts
  // `surface-coral`, so ice is legal here.
  { route: "/dashboard", surface: "surface-ice", role: "list" },
  { route: "/dashboard", surface: "surface-lime", role: "countdown" },
  { route: "/anniversaries", surface: "surface-lime", role: "number" },
]);

/**
 * The anniversaries accents as a named pair, so `HeroSection` indexes a
 * declaration rather than hard-coding `surface-lime` / `surface-aqua`.
 *
 * A tuple rather than an array lookup by index into `ACCENT_SLOTS`: the order
 * *is* the meaning (days first, upcoming second), and a tuple says so in the
 * type. `undefined` on a missing row is deliberate — a screen with one accent
 * renders one card rather than an empty placeholder.
 */
export const ACCENT_ANNIVERSARIES = Object.freeze({
  days: ACCENT_SLOTS.find((s) => s.route === "/anniversaries" && s.role === "number"),
});

/** The dashboard's accents, by the same keying. */
export const ACCENT_DASHBOARD = Object.freeze({
  /** The weather card. W57.0 — it used to paint `surface-ice` by hand. */
  weather: ACCENT_SLOTS.find((s) => s.route === "/dashboard" && s.role === "list"),
  countdown: ACCENT_SLOTS.find((s) => s.route === "/dashboard" && s.role === "countdown"),
});

/**
 * Saturated accent cards allowed on one screen.
 *
 * 3 is what the reference's Home screen shows (lilac / coral / lime below one
 * pale hero) and it is also the whole card — a fourth would reuse a colour and
 * the accents would stop reading as accents. W42 allowed **1**; `ADR-0109`
 * reversed that to 3.
 *
 * W44 lowered the *used* count without lowering this cap: the cap is what the
 * palette can carry, and `/anniversaries` simply now uses one of its three.
 * Dropping the cap to 1 would re-forbid the dashboard's two, which is the very
 * density the reversal was about.
 */
export const MAX_ACCENT_CARDS = 3;

/**
 * The four heroes as named constants.
 *
 * Components import `HERO_DASHBOARD` rather than calling a lookup, so a renamed
 * route is a **type error** instead of an `undefined` that renders a card with no
 * fill. The lookup throws for the same reason — a missing declaration should stop
 * the build, not ship a colourless card.
 */
function heroFor(route: string): HeroSlot {
  const found = HERO_SLOTS.find((s) => s.route === route);
  if (!found) {
    throw new Error(
      `HERO_SLOTS has no entry for ${route}. A screen that carries a mascot card needs ` +
        `a row here — add the row rather than reaching for a bare surface-* class, which ` +
        `is how W41.0 ended up with three opt-ins across 39 routes.`,
    );
  }
  return found;
}

export const HERO_DASHBOARD = heroFor("/dashboard");
export const HERO_ANNIVERSARIES = heroFor("/anniversaries");
/**
 * W53.2 D-6. See `DELIBERATE_NO_HERO` for why the other three detail pages
 * have no counterpart to this constant — the absence there is a decision.
 */
export const HERO_ANNIVERSARY_DETAIL = heroFor("/anniversaries/:id");
/**
 * The diaries hero, and the reason this comment exists: the row key is
 * `/diaries/:id`, not `/diaries`.
 *
 * Changing the key without changing this call made the lookup **throw at module
 * load**, which took down every one of the 39 routes at once — the screen-type
 * table is imported by `AppShell`, so one bad key is a white-screen-everywhere
 * bug rather than a one-page one. `scripts/w42-route-audit.mjs` caught it on the
 * next run and reported 「neither MobileHeader nor PillNav rendered」 27 times,
 * which is what a module-load throw looks like from the outside.
 */
export const HERO_DIARIES = heroFor("/diaries/:id");
export const HERO_WISHES = heroFor("/wishes");

/** The accent surfaces declared for a screen path, in render order. */
export function accentSurfacesFor(pathname: string): readonly AccentSurface[] {
  return ACCENT_SLOTS.filter((s) => matchesScreen(pathname, s.route)).map((s) => s.surface);
}

/**
 * The mascot's own body colour, by name.
 *
 * Transcribed from `mascots.ts` `spec.fill` on purpose rather than imported: the
 * point of this check is that the two tables are **independently** declared, so
 * a change to one does not silently satisfy the other. If they were derived from
 * each other the assertion would be a tautology. `mascots.test.tsx` pins the
 * values, so a drift here fails there.
 */
const MASCOT_FILL: Record<HeroSlot["mascot"], string> = {
  coral: "#F34A42",
  aqua: "#4AC2C6",
  lilac: "#A88BFF",
  lime: "#CDFB64",
};

/** The fill a `surface-*` peak tier paints. Same reasoning as `MASCOT_FILL`. */
const SURFACE_FILL: Record<AccentSurface, string> = {
  "surface-aqua": "#4AC2C6",
  "surface-lime": "#CDFB64",
  "surface-ice": "#8CC8EE",
  "surface-lilac": "#A88BFF",
  "surface-coral": "#F34A42",
};

/** The pale hero fill. One token, spent once per screen (see ADR-0109). */
const TINT_FILL: Record<HeroSlot["tint"], string> = {
  "tint-hero": "#E3F3FF",
};

/**
 * WCAG relative luminance, so the F17 check can ask a second question.
 *
 * Written out rather than imported: the contrast gate in
 * `docs/audit/contrast-matrix.mjs` is the authority for the app's colour pairs,
 * and this is a two-line restatement of the same formula for one local decision.
 * If they ever disagree, that script's numbers are the ones to believe.
 */
function relLuminance(hex6: string): number {
  const ch = (c: number) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const n = parseInt(hex6.replace("#", ""), 16);
  const r = ch((n >> 16) & 255);
  const g = ch((n >> 8) & 255);
  const b = ch(n & 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(a: string, b: string): number {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * F17 / `DESIGN.md §18.0`: 「a character never shares its card's colour」.
 *
 * Takes the card's **actual fill** rather than a class name, because a class name
 * is a promise and a hex is a fact — and the first version of `HeroCard` passed
 * the literal string `"surface-aqua"` as a placeholder, which made the
 * anniversaries hero throw on its own aqua mascot. A check that fires on correct
 * input is worse than no check: it teaches the next person to work around it.
 *
 * **Two questions, not one.** The screenshot pass caught that "not the same
 * colour" is not sufficient: `aqua` `#4AC2C6` on the pale hero `#E3F3FF` is a
 * different colour and still nearly invisible — it is 1.31:1. A mascot is a
 * **graphical object** (WCAG SC 1.4.11), so the bar is 3:1, not AA body. F17
 * alone would have let it through, which is why the rule needed the second
 * question rather than just more care.
 */
export function assertMascotContrast(
  cardFill: string,
  mascot: HeroSlot["mascot"],
): void {
  // Compare against **every** fill this module knows — the pale hero and the five
  // saturated accents — rather than one of them. Comparing against a single
  // caller-chosen value is how the placeholder bug above happened.
  const known: Record<string, string> = { ...SURFACE_FILL, ...TINT_FILL };
  const resolved = known[cardFill] ?? cardFill;
  const body = MASCOT_FILL[mascot];

  if (resolved === body) {
    throw new Error(
      `${mascot} on ${cardFill} (${resolved}) would draw the character in its own ` +
        `card's colour — F17 requires a different one`,
    );
  }

  const ratio = contrastRatio(resolved, body);
  if (ratio < 3) {
    throw new Error(
      `${mascot} on ${cardFill} is ${ratio.toFixed(2)}:1 — a mascot is a graphical ` +
        `object (WCAG SC 1.4.11) and needs 3:1 against its own card. Different ` +
        `colour is not the same as visible.`,
    );
  }
}

/** The hex a hero's pale fill resolves to. For callers that need the pair. */
export function heroFillFor(slot: HeroSlot): string {
  return TINT_FILL[slot.tint];
}

/**
 * F13: `coral` is 4.76:1, which clears AA only at 700+ weight. It is legal on a
 * card carrying a bare numeral and illegal on one carrying body copy, so the
 * check is against the declared **role** rather than against the colour alone.
 */
export function assertCoralRole(slot: AccentSlot): void {
  if (slot.surface === "surface-coral" && slot.role !== "number") {
    throw new Error(
      `surface-coral is only legal on a card carrying a bare numeral (F13) — ` +
        `"${slot.route}" declares role="${slot.role}", which carries body copy`,
    );
  }
}

/** Prefix match with a trailing-slash boundary, mirroring `isWithinModule`. */
function withinRoot(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

/**
 * Classify a pathname. Pure, so both the provider and the tests read the same
 * function rather than one re-deriving the rule.
 */
export function screenTypeForPath(pathname: string): ScreenType {
  // Exceptions first. A feeling root is a prefix, so without this ordering
  // `/dashboard/messages` could never be anything but `feeling`.
  if (DATA_ROUTE_ROOTS.some((root) => withinRoot(pathname, root))) {
    return SCREEN_DATA;
  }
  return FEELING_ROUTE_ROOTS.some((root) => withinRoot(pathname, root))
    ? SCREEN_FEELING
    : DEFAULT_SCREEN_TYPE;
}

/**
 * Does `pathname` land on `route`?
 *
 * Two different rules, because the table mixes two kinds of key and conflating
 * them is what produced a screen's worth of phantom F16 violations:
 *
 *  - **A static key matches exactly.** `/anniversaries` is the anniversaries
 *    board. `/anniversaries/new` is a create form and `/anniversaries/trash` is
 *    a trash list — neither is the board, and a prefix match hands them the lime
 *    hero card and the aqua character. The table names screens, so it matches
 *    screens.
 *  - **A `:param` key is a wildcard**, with one thing a regex cannot know:
 *    React Router ranks a static segment above a dynamic one, so `/diaries/:id`
 *    is not `/diaries/new` and not `/diaries/trash`. `STATIC_SIBLINGS` restates
 *    that, and `w42-screen-type.test.ts` cross-checks it against `App.tsx`.
 */
function matchesScreen(
  pathname: string,
  route: string,
  staticSiblings: readonly string[] = [],
): boolean {
  if (!route.includes(":")) return pathname === route;
  if (staticSiblings.some((s) => withinRoot(pathname, s))) return false;
  return new RegExp(`^${route.replace(/:[^/]+/g, "[^/]+")}(/.*)?$`).test(pathname);
}

/**
 * Sibling routes that outrank a `:param` row sharing their parent — React
 * Router's static-before-dynamic ranking, restated.
 *
 * `/diaries/:id` is a dynamic row, and `/diaries/new` + `/diaries/trash` are
 * static siblings of it. A regex cannot know that, so it is declared. The router's
 * own table is the source of truth and `w42-screen-type.test.ts` **cross-checks
 * this list against `App.tsx`**, so adding a third static sibling to `/diaries`
 * without listing it here fails a test rather than silently making the create
 * form claim the detail screen's ice peak card.
 */
const STATIC_SIBLINGS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  "/diaries/:id": Object.freeze(["/diaries/new", "/diaries/trash"]),
  // W53.2 D-6 — added with the row, not after. `w42-screen-type.test.ts`
  // cross-checks this list against `App.tsx`, so registering
  // `/anniversaries/:id` as a hero without registering its two static siblings
  // would hand the create form and the trash list the detail page's mascot.
  // The list is **derived from the router**, not from what a reader remembers.
  "/anniversaries/:id": Object.freeze(["/anniversaries/new", "/anniversaries/trash"]),
});

/** The hero slot for a pathname, or `null` when that screen has none. */
export function heroForPath(pathname: string): HeroSlot | null {
  // The table is a **screen** list, so it is not filtered through
  // `screenTypeForPath`: a data screen can legitimately host a hero in future,
  // and the two decisions are different ones. What it *is* filtered by is the
  // data exception list, because `/dashboard/messages` sits inside a hero route
  // and must not inherit it.
  if (DATA_ROUTE_ROOTS.some((root) => withinRoot(pathname, root))) return null;
  return (
    HERO_SLOTS.find((slot) =>
      matchesScreen(pathname, slot.route, STATIC_SIBLINGS[slot.route] ?? []),
    ) ?? null
  );
}

/** Is this a hero screen? F16's "one pale card" is about these and only these. */
export function isHeroScreen(pathname: string): boolean {
  return heroForPath(pathname) !== null;
}

/**
 * Defaults to `data` so a component rendered outside the provider — every
 * isolated unit test in the suite, and any future subtree mounted outside
 * AppShell — resolves instead of throwing. A test that renders `<DiaryCard />`
 * on its own should not have to build a router to get a screen type.
 */
export const ScreenTypeContext = createContext<ScreenType>(DEFAULT_SCREEN_TYPE);

/** The current screen's type. Never throws; see the context default above. */
export function useScreenType(): ScreenType {
  return useContext(ScreenTypeContext);
}

/** Re-exported so the provider file needs one import, not two. */
export type { ReactNode };
